import * as Sentry from "@sentry/nextjs";
import type { AxiosError, AxiosRequestConfig } from "axios";
import axios from "axios";

import { shouldSkipApiRequestForRobot } from "~/utils";
import { wasClientUnreachable } from "~/utils/clientContext";
import type { ClientRequestTiming } from "~/utils/clientContext";

/**
 * Единая точка КЛИЕНТСКИХ (браузерных) API-запросов и репорта их ошибок в
 * GlitchTip. Всё, что нужно знать про клиентский запрос, — здесь, в api/client,
 * чтобы не прыгать по utils.
 *
 * Ошибка помечается:
 *   - tag `side: client`  — сразу видно, что это браузер, а не SSR;
 *   - tag `api: <имя>`    — какой именно вызов упал (sections.getSections и т.п.);
 *   - fingerprint по `api` + статусу — каждый эндпоинт это отдельная issue в
 *     GlitchTip, а не «всё в кучу»;
 *   - extra — url, method, params и любые доп. данные вызова.
 */

/**
 * Дефолтный таймаут клиентского запроса. Без него axios ждёт ответ бесконечно,
 * и зависший бэкенд не даёт никакого сигнала. С таймаутом зависание превращается
 * в явный статус `timeout` (см. describeError). Явный timeout в axiosConfig
 * вызывающего имеет приоритет над дефолтом.
 */
const DEFAULT_CLIENT_TIMEOUT_MS = 20000;

export interface ClientApiCallOptions {
  /** Имя вызова для группировки ошибок, напр. "sections.getSections". */
  api: string;
  /** HTTP-метод, по умолчанию "get". */
  method?: "get" | "post";
  /** Query (для get) или тело (для post). */
  params?: Record<string, unknown>;
  /** Пропустить запрос для ботов/краулеров (Bytespider и т.п.). */
  skipForRobots?: boolean;
  /**
   * Критичный для бизнеса вызов: оформление заказа, мутации корзины, отправка
   * формы, ссылка на оплату. К таким вызовам не применяются отсечки шума —
   * сообщаем в GlitchTip о любой ошибке, включая отмену запроса, 403 от защиты
   * периметра и обрыв связи (последний иначе съедается глобальным фильтром,
   * см. reportLabel ниже). Потерянный заказ дороже чистоты списка issue.
   */
  critical?: boolean;
  /**
   * Отправка заявки или заказа: сбой = потерянный клиент, в GlitchTip уходит
   * с уровнем fatal. Данные заявки должны быть в params/extra — если запрос не
   * дошёл до Битрикса, в его журнале (log/leads.log) её нет, восстанавливать
   * придётся по событию.
   */
  lead?: boolean;
  /** Доп. данные, которые уйдут в GlitchTip вместе с ошибкой. */
  extra?: Record<string, unknown>;
  /** Доп. конфиг axios (headers, withCredentials, ...). */
  axiosConfig?: AxiosRequestConfig;
}

/** Ошибка клиентского API — даёт понятный заголовок в сборщике ошибок. */
class ClientApiError extends Error {
  constructor(
    message: string,
    public readonly reason?: unknown,
  ) {
    super(message);
    this.name = "ClientApiError";
  }
}

/**
 * Классификация ошибки axios для осмысленной группировки в GlitchTip:
 *  - timeout — бэкенд не ответил вовремя (реальный сигнал «тупит»), отдельная issue;
 *  - aborted — запрос отменён (уход со страницы, закрытие вкладки) — не ошибка, шум;
 *  - <код>   — сервер ответил ошибочным статусом (4xx/5xx);
 *  - network — ответ вообще не дошёл (обрыв связи, оффлайн) — шум клиента,
 *              отсекается в sentry.filters по "Network Error".
 *
 * Важно: axios 0.26 ставит код ECONNABORTED И на таймаут, И на отмену запроса
 * ("Request aborted"). Различаем по сообщению — иначе аборты (в т.ч. от ботов
 * вроде YaDirectFetcher) сыплются как ложные таймауты.
 */
function describeError(error: unknown): {
  statusKey: string;
  label: string;
  status?: number;
} {
  if (axios.isAxiosError(error)) {
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return /timeout/i.test(error.message || "")
        ? { statusKey: "timeout", label: "timeout" }
        : { statusKey: "aborted", label: "aborted" };
    }
    const status = error.response?.status;
    if (status) {
      return { statusKey: String(status), label: `HTTP ${status}`, status };
    }
  }
  return { statusKey: "network", label: "network error" };
}

function reportError(
  url: string,
  opts: ClientApiCallOptions,
  error: unknown,
  timing: ClientRequestTiming,
): void {
  const method = (opts.method ?? "get").toUpperCase();
  const { statusKey, label, status } = describeError(error);

  // Отсечки шума — только для обычных вызовов. Для critical (оформление заказа,
  // мутации корзины, отправка форм) любая из этих «мелочей» означает потерянного
  // клиента, и мы обязаны о ней узнать.
  if (!opts.critical) {
    // Отмена запроса (уход со страницы, закрытие вкладки, бот ушёл) — не ошибка.
    if (statusKey === "aborted") {
      return;
    }

    // Вкладку сворачивали/экран блокировали/клиент оффлайн: запрос не доехал
    // не по вине бэкенда, репортить нечего (см. utils/clientContext).
    if (
      (statusKey === "timeout" || statusKey === "network") &&
      wasClientUnreachable(timing)
    ) {
      return;
    }

    // Вердикт защиты периметра (перед сайтом стоит DDoS-Guard), а не поломка
    // приложения: 403/401 получает клиент, не прошедший проверку, — как правило
    // скрейпер с подделанным User-Agent, которого не ловит skipForRobots.
    //
    // Глушим ТОЛЬКО на вызовах с skipForRobots: этот флаг и означает «эндпоинт,
    // который массово долбят краулеры» — там 403 это шум тысячами (см. историю
    // sections.getSections). На остальных вызовах 403/401 репортим: их делает
    // живой человек, и блокировка реальных пользователей (подсеть, мобильный
    // оператор) — ровно то, о чём нужно узнать сразу.
    if (opts.skipForRobots && (statusKey === "403" || statusKey === "401")) {
      return;
    }
  }

  // Текст "network error" намеренно отсекается глобальными фильтрами Sentry
  // (ignoreErrors и beforeSend в sentry.filters.js) — обрыв связи у случайного
  // посетителя нам не интересен. Но для critical-вызова это ровно тот случай,
  // который нужно видеть, поэтому даём другую формулировку, под фильтр не
  // подпадающую.
  const reportLabel =
    opts.critical && statusKey === "network" ? "no response" : label;

  const message = (error as AxiosError)?.message ?? String(error);

  Sentry.captureException(
    new ClientApiError(`[client] ${opts.api} failed (${reportLabel})`, error),
    {
      level: opts.lead ? "fatal" : "error",
      tags: {
        side: "client",
        api: opts.api,
        apiUrl: url,
        method,
        status: statusKey,
      },
      extra: {
        url,
        method,
        status,
        params: opts.params,
        message,
        // Сколько запрос реально шёл. При зависшем бэкенде упирается в таймаут,
        // при уснувшей вкладке — минуты. Первое, на что смотреть в issue.
        elapsedMs: timing.elapsedMs,
        ...opts.extra,
      },
      // Отдельная issue на каждый (api + статус), а не общий ворох.
      // timeout получает свой fingerprint и не тонет среди сетевых ошибок.
      fingerprint: ["client-api", opts.api, statusKey],
    },
  );
}

/**
 * Результат вызова с явным признаком неудачи. Нужен там, где `null` — это
 * валидный ответ бэкенда, и его нельзя путать с «бэкенд не ответил»:
 * например, `user.getUser` отдаёт пустой ответ и гостю, и при таймауте, а
 * кешировать как «гость» можно только первое (см. redux/action-creaters/user).
 */
export interface ClientApiResult<T> {
  data: T | null;
  /** Запрос не выполнен: таймаут, обрыв, ошибочный статус. */
  failed: boolean;
}

/**
 * Выполнить клиентский API-запрос. Возвращает данные ответа, либо `null` —
 * при пропуске для бота или при ошибке (ошибка уходит в GlitchTip с контекстом).
 */
export async function clientApiCall<T = any>(
  url: string,
  opts: ClientApiCallOptions,
): Promise<T | null> {
  return (await clientApiCallWithStatus<T>(url, opts)).data;
}

/** То же, что clientApiCall, но с признаком неудачи (см. ClientApiResult). */
export async function clientApiCallWithStatus<T = any>(
  url: string,
  opts: ClientApiCallOptions,
): Promise<ClientApiResult<T>> {
  if (opts.skipForRobots && shouldSkipApiRequestForRobot()) {
    return { data: null, failed: false };
  }

  const startedAt = Date.now();
  const timeoutMs = opts.axiosConfig?.timeout ?? DEFAULT_CLIENT_TIMEOUT_MS;

  try {
    const method = opts.method ?? "get";
    const response =
      method === "get"
        ? await axios.get(url, {
            timeout: DEFAULT_CLIENT_TIMEOUT_MS,
            params: opts.params,
            ...opts.axiosConfig,
          })
        : await axios.post(url, opts.params, {
            timeout: DEFAULT_CLIENT_TIMEOUT_MS,
            ...opts.axiosConfig,
          });
    return { data: response.data as T, failed: false };
  } catch (error) {
    reportError(url, opts, error, {
      startedAt,
      elapsedMs: Date.now() - startedAt,
      timeoutMs,
    });
    return { data: null, failed: true };
  }
}
